import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { toast } from "sonner";
import sounds from "@/lib/sounds";
import { ArrowLeft, Coins, Lock, Check, ShoppingBag } from "lucide-react";

const TIER_LABELS = { 1: "Ship Colors", 2: "Ship Decals", 3: "Laser Upgrades", 4: "Trail Effects", 5: "Explosion Effects" };
const TIER_DESC = {
  1: "Change your ship's hull color — your laser matches!",
  2: "Add a decal emblem on your ship",
  3: "Upgrade your weapon's look and feel",
  4: "Leave a stylish trail behind your ship",
  5: "Customize your explosion effects",
};
const TIER_MIN_BANDS = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 3 };

function ColorSwatch({ color, size = 24 }) {
  return <div style={{ width: size, height: size, borderRadius: 4, background: color, boxShadow: `0 0 8px ${color}40` }} />;
}

export default function ShopPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [coins, setCoins] = useState(0);
  const [loadout, setLoadout] = useState({});
  const [inventory, setInventory] = useState([]);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    const load = async () => {
      const [shopRes, loadoutRes] = await Promise.all([
        API.get("/shop/items"),
        API.get("/shop/loadout"),
      ]);
      setItems(shopRes.data.items);
      setCoins(shopRes.data.coins);
      setLoadout(loadoutRes.data.loadout);
      setInventory(loadoutRes.data.inventory);
    };
    load();
  }, []);

  const buyItem = async (itemId) => {
    setBuying(itemId);
    try {
      const { data } = await API.post("/shop/purchase", { item_id: itemId });
      sounds.correct();
      setCoins(data.coins);
      setInventory((prev) => [...prev, data.item_id]);
      setItems((prev) => prev.map((i) => i.id === itemId ? { ...i, owned: true } : i));
      toast.success(data.message);
      refreshUser();

      // Auto-equip the purchased item
      const purchasedItem = items.find((i) => i.id === itemId);
      if (purchasedItem) {
        const fieldMap = { ship_color: "ship_color", decal: "decal", laser: "laser", trail: "trail", explosion: "explosion" };
        const field = fieldMap[purchasedItem.type];
        if (field) {
          try {
            const eqRes = await API.put("/shop/loadout", { ...loadout, [field]: purchasedItem.value });
            setLoadout(eqRes.data.loadout);
            toast.success(`${purchasedItem.name} equipped!`);
          } catch { /* equip failed silently, user can equip manually */ }
        }
      }
    } catch (err) {
      sounds.wrong();
      toast.error(err.response?.data?.detail || "Purchase failed");
    } finally {
      setBuying(null);
    }
  };

  const equipItem = async (item) => {
    const fieldMap = { ship_color: "ship_color", decal: "decal", laser: "laser", trail: "trail", explosion: "explosion" };
    const field = fieldMap[item.type];
    if (!field) return;
    const isEquipped = loadout[field] === item.value;
    const newLoadout = { ...loadout, [field]: isEquipped ? null : item.value };
    try {
      const { data } = await API.put("/shop/loadout", newLoadout);
      setLoadout(data.loadout);
      sounds.shoot();
      toast.success(isEquipped ? `Unequipped ${item.name}` : `Equipped ${item.name}`);
    } catch { toast.error("Failed to update loadout"); }
  };

  const groupedByTier = [1, 2, 3, 4, 5].map((tier) => ({
    tier,
    label: TIER_LABELS[tier],
    desc: TIER_DESC[tier],
    minBand: TIER_MIN_BANDS[tier],
    unlocked: (user?.current_skill_band || 0) >= TIER_MIN_BANDS[tier],
    items: items.filter((i) => i.tier === tier),
  }));

  return (
    <div className="min-h-screen p-6 lg:p-10 max-w-5xl mx-auto" data-testid="shop-page">
      <div className="flex items-center justify-between mb-10">
        <div>
          <button onClick={() => navigate("/student")} className="flex items-center gap-2 text-white/40 hover:text-white mb-3 font-body text-sm transition-colors duration-200" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white tracking-tighter">
            <span className="neon-text-cyber">Hangar</span> Shop
          </h1>
        </div>
        <div className="flex items-center gap-2 glass-surface px-5 py-3 rounded-xl" data-testid="coin-balance">
          <Coins className="w-5 h-5 text-yellow-400" />
          <span className="font-mono text-2xl font-bold text-yellow-400">{coins}</span>
        </div>
      </div>

      {groupedByTier.map(({ tier, label, desc, minBand, unlocked, items: tierItems }) => (
        <div key={tier} className="mb-8" data-testid={`shop-tier-${tier}`}>
          <div className="flex items-center gap-3 mb-4">
            {unlocked ? <ShoppingBag className="w-5 h-5 text-cyber" /> : <Lock className="w-5 h-5 text-white/20" />}
            <div>
              <h2 className={`font-heading text-lg font-bold ${unlocked ? "text-white" : "text-white/30"}`}>
                Tier {tier}: {label}
              </h2>
              <p className="font-body text-xs text-white/40">
                {unlocked ? desc : `Unlock at Skill Band ${minBand + 1}`}
              </p>
            </div>
          </div>

          <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 ${!unlocked ? "opacity-40 pointer-events-none" : ""}`}>
            {tierItems.map((item) => {
              const owned = item.owned || inventory.includes(item.id);
              const fieldMap = { ship_color: "ship_color", decal: "decal", laser: "laser", trail: "trail", explosion: "explosion" };
              const equipped = loadout[fieldMap[item.type]] === item.value;
              return (
                <div
                  key={item.id}
                  data-testid={`shop-item-${item.id}`}
                  className={`p-4 rounded-xl border transition-all duration-200 ${
                    equipped ? "border-cyber bg-cyber/5 ship-card-selected"
                    : owned ? "border-white/10 bg-white/[0.02]"
                    : "border-white/5 hover:border-white/15"
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {item.type === "ship_color" && <ColorSwatch color={item.value} />}
                      <span className="font-body text-sm font-medium text-white">{item.name}</span>
                    </div>
                    {equipped && <span className="font-mono text-[10px] text-cyber uppercase tracking-wider">Equipped</span>}
                  </div>

                  {owned ? (
                    <button
                      onClick={() => equipItem(item)}
                      data-testid={`equip-${item.id}`}
                      className={`w-full py-2 rounded-lg font-heading text-xs tracking-wider transition-colors duration-200 ${
                        equipped ? "bg-white/10 text-white/50 hover:bg-white/5" : "btn-cyber"
                      }`}
                    >
                      {equipped ? "UNEQUIP" : "EQUIP"}
                    </button>
                  ) : (
                    <button
                      onClick={() => buyItem(item.id)}
                      disabled={buying === item.id || coins < item.price || !unlocked}
                      data-testid={`buy-${item.id}`}
                      className="w-full py-2 rounded-lg font-heading text-xs tracking-wider bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/20 transition-colors duration-200 disabled:opacity-30 flex items-center justify-center gap-2"
                    >
                      <Coins className="w-3 h-3" />
                      {buying === item.id ? "..." : item.price}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
